/**
 * 采集按钮业务埋点组件
 *
 * 封装采集相关的埋点逻辑，包括：
 * - 采集/取消采集/快采/选择画板等不同状态
 * - 自动处理数据格式转换（ID 转字符串）
 * - 自动处理 content_type 和 ext 数据
 * - 自动处理 module_name 和 search_word（条件传入）
 */

import { getContentType } from "@/utils/getContentType";
import React from "react";
import { Track } from "../components/Track";

export interface CollectButtonProps {
  /**
   * 采集按钮状态
   */
  buttonName: "采集" | "取消采集" | "快采" | "选择画板" | "撤销采集";

  /**
   * Pin 数据
   */
  pin?: {
    pin_id?: string | number;
    board_id?: string | number;
    file_id?: string | number;
    extra?: any;
    ext?: Record<string, any>;
  };

  /**
   * 模块名称（可选）
   */
  moduleName?: string;

  /**
   * 搜索词（可选，通常由 SearchContext 自动注入）
   */
  searchWord?: string;

  /**
   * 子元素（按钮组件）
   */
  children: React.ReactNode;

  /**
   * 是否启用曝光埋点
   * @default true
   */
  enableExpose?: boolean;

  /**
   * 曝光阈值（0-1），默认 0.6（60%）
   */
  exposeThreshold?: number;

  /**
   * 曝光时长（ms），默认 1500ms
   */
  exposeTime?: number;
}

/**
 * 采集按钮业务埋点组件
 *
 * @example
 * ```tsx
 * <CollectButton
 *   buttonName={isPinCreated ? "取消采集" : "采集"}
 *   pin={pin}
 *   moduleName={moduleName}
 * >
 *   <Button onClick={handleClick}>
 *     {isPinCreated ? "已采集" : "采集"}
 *   </Button>
 * </CollectButton>
 * ```
 */
export function CollectButton({
  buttonName,
  pin,
  moduleName,
  searchWord,
  children,
  enableExpose = true,
  exposeThreshold,
  exposeTime,
}: CollectButtonProps) {
  // 构建埋点数据
  const trackData: Record<string, any> = {
    button_name: buttonName,
  };

  // 处理 ID 字段（确保是字符串类型）
  if (pin?.pin_id !== undefined) {
    trackData.pin_id = String(pin.pin_id);
  }
  if (pin?.board_id !== undefined) {
    trackData.board_id = String(pin.board_id);
  }
  if (pin?.file_id !== undefined) {
    trackData.file_id = String(pin.file_id);
  }

  // 处理 content_type
  if (pin) {
    trackData.content_type = getContentType(pin as any);
  }

  // 处理 ext 数据
  if (pin?.ext) {
    Object.assign(trackData, pin.ext);
  }

  // 条件传入 module_name 和 search_word（避免 undefined）
  if (moduleName) {
    trackData.module_name = moduleName;
  }
  if (searchWord) {
    trackData.search_word = searchWord;
  }

  return (
    <Track
      event="button_click"
      data={trackData}
      exposeEvent={enableExpose ? "button_expose" : undefined}
      exposeThreshold={exposeThreshold}
      exposeTime={exposeTime}
    >
      {children}
    </Track>
  );
}
